import type { ReactNode } from 'react';
import { ErrorBoundary, type FallbackProps } from 'react-error-boundary';
import { notify } from '@core/notify/toast';

/**
 * Replaces `core/handlers/global-error.handler.ts`.
 *
 * Same division of labour as the Angular version: this handles RENDER and lifecycle
 * crashes only. HTTP errors are owned by the axios response interceptor — the
 * Angular handler explicitly skipped `HttpErrorResponse` for the same reason
 * (global-error.handler.ts, the `instanceof HttpErrorResponse` early return).
 */
function CrashScreen({ error, resetErrorBoundary }: FallbackProps) {
  const message = error instanceof Error ? error.message : String(error);

  return (
    <div
      role="alert"
      className="flex min-h-[60vh] flex-col items-center justify-center gap-4 p-8 text-center"
    >
      <span className="material-icons text-[36px] text-danger">error_outline</span>
      <h1 className="page-title">Something went wrong</h1>
      <p className="page-subtitle max-w-[520px]">{message}</p>
      <div className="mt-2 flex gap-3">
        <button
          type="button"
          onClick={resetErrorBoundary}
          className="inline-flex min-h-8 items-center gap-1.5 rounded-sm bg-accent px-4 text-base font-medium text-white transition-colors hover:bg-accent-hover"
        >
          Try again
        </button>
        <button
          type="button"
          onClick={() => window.location.assign('/dashboard')}
          className="inline-flex min-h-8 items-center gap-1.5 rounded-sm border border-line-strong bg-surface px-4 text-base font-medium text-fg transition-colors hover:bg-surface-2"
        >
          Back to dashboard
        </button>
      </div>
    </div>
  );
}

export function AppErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary
      FallbackComponent={CrashScreen}
      onError={(error: unknown, info) => {
        console.error('Unhandled render error', error, info.componentStack);
        const message = error instanceof Error ? error.message : String(error);
        notify.error(message || 'Something went wrong');
      }}
    >
      {children}
    </ErrorBoundary>
  );
}
